import React, { Component } from "react";
import { Card, Badge, Table, Button, Modal, Alert, Tabs, Tab } from "react-bootstrap";

export default class ImportResultsReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showDetailModal: false,
      selectedCategory: null,
      detailData: [],
    };
  }

  openDetailModal = (category, data) => {
    this.setState({
      showDetailModal: true,
      selectedCategory: category,
      detailData: data || [],
    });
  };

  closeDetailModal = () => {
    this.setState({
      showDetailModal: false,
      selectedCategory: null,
      detailData: [],
    });
  };

  formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  renderSummaryCards = () => {
    const { results } = this.props;
    
    const cards = [
      {
        title: "Successfully Created",
        count: results.insert_count || 0,
        variant: "success",
        icon: "fas fa-plus-circle",
        description: "New records added to database",
        category: "created"
      },
      {
        title: "Successfully Updated",
        count: results.update_count || 0,
        variant: "info",
        icon: "fas fa-edit",
        description: "Existing records modified",
        category: "updated"
      },
      {
        title: "Skipped Records",
        count: results.skip_count || 0,
        variant: "warning",
        icon: "fas fa-exclamation-triangle",
        description: "Records skipped due to business rules",
        category: "skipped"
      },
      {
        title: "Failed Records",
        count: results.failed_count || 0,
        variant: "danger",
        icon: "fas fa-times-circle",
        description: "Records that couldn't be processed",
        category: "failed"
      }
    ];

    return (
      <div className="row mb-4">
        {cards.map((card, index) => (
          <div key={index} className="col-md-3 col-sm-6 mb-3">
            <Card 
              className={`h-100 border-${card.variant} cursor-pointer hover-shadow`}
              onClick={() => this.openDetailModal(card.category, results[`${card.category}_details`])}
            >
              <Card.Body className="text-center">
                <div className={`text-${card.variant} mb-2`}>
                  <i className={`${card.icon} fa-2x`}></i>
                </div>
                <h3 className={`text-${card.variant} mb-1`}>{card.count}</h3>
                <h6 className="mb-1">{card.title}</h6>
                <p className="small text-muted mb-0">{card.description}</p>
                {card.count > 0 && (
                  <div className="mt-2">
                    <Badge bg={card.variant} className="small">
                      Click for details
                    </Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
    );
  };

  renderProcessingSummary = () => {
    const { results, session } = this.props;
    const totalProcessed = (results.insert_count || 0) + (results.update_count || 0) + 
                          (results.skip_count || 0) + (results.failed_count || 0);
    const successRate = session.totalRows > 0 ? 
      Math.round(((results.insert_count || 0) + (results.update_count || 0)) / session.totalRows * 100) : 0;

    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-chart-pie me-2"></i>
            Processing Summary
          </h5>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <Table size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <td><strong>Total Rows in File:</strong></td>
                    <td>{session.totalRows || 0}</td>
                  </tr>
                  <tr>
                    <td><strong>Rows Processed:</strong></td>
                    <td>{session.processedRows || totalProcessed}</td>
                  </tr>
                  <tr>
                    <td><strong>Success Rate:</strong></td>
                    <td>
                      <Badge bg={successRate >= 90 ? 'success' : successRate >= 70 ? 'warning' : 'danger'}>
                        {successRate}%
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Processing Time:</strong></td>
                    <td>{results.processing_time || "N/A"}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
            <div className="col-md-6">
              <Table size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <td><strong>Session Code:</strong></td>
                    <td><Badge bg="primary">{session.sessionCode}</Badge></td>
                  </tr>
                  <tr>
                    <td><strong>Target Table:</strong></td>
                    <td><Badge bg="info">{session.targetTable}</Badge></td>
                  </tr>
                  <tr>
                    <td><strong>File Name:</strong></td>
                    <td>{session.fileName}</td>
                  </tr>
                  <tr>
                    <td><strong>Import Mode:</strong></td>
                    <td>
                      <Badge bg={session.importMode === 'valid_only' ? 'success' : 
                                session.importMode === 'force_all' ? 'warning' : 'secondary'}>
                        {session.importMode === 'valid_only' ? 'Valid Only' :
                         session.importMode === 'force_all' ? 'Force All' : 'Unknown'}
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  renderDetailTable = (data, category) => {
    if (!data || data.length === 0) {
      return (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          No detailed data available for {category} records.
        </Alert>
      );
    }

    const getVariantForCategory = (cat) => {
      switch(cat) {
        case 'created': return 'success';
        case 'updated': return 'info';
        case 'skipped': return 'warning';
        case 'failed': return 'danger';
        default: return 'secondary';
      }
    };

    return (
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <Table striped bordered hover size="sm">
          <thead className={`table-${getVariantForCategory(category)}`}>
            <tr>
              <th>Row #</th>
              <th>Data</th>
              <th>Reason/Message</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index}>
                <td>
                  <Badge bg="secondary">{item.row_number || index + 1}</Badge>
                </td>
                <td>
                  <div className="small">
                    {typeof item.data === 'object' ? 
                      Object.entries(item.data).slice(0, 3).map(([key, value]) => (
                        <div key={key}><strong>{key}:</strong> {value || 'NULL'}</div>
                      )) :
                      item.data || 'No data'
                    }
                    {typeof item.data === 'object' && Object.keys(item.data).length > 3 && (
                      <div className="text-muted">... and {Object.keys(item.data).length - 3} more fields</div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="small">
                    {item.message || item.error || item.reason || 'No message available'}
                    {item.validation_errors && (
                      <div className="text-danger mt-1">
                        <strong>Validation Errors:</strong>
                        <ul className="mb-0 mt-1">
                          {Object.entries(item.validation_errors).map(([field, errors]) => (
                            <li key={field}>{field}: {Array.isArray(errors) ? errors.join(', ') : errors}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </td>
                <td className="small">
                  {this.formatDateTime(item.timestamp || item.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  renderActionButtons = () => {
    const { session, onDownloadReport, onRetryFailed, onClose } = this.props;
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          {onDownloadReport && (
            <Button
              variant="outline-primary"
              onClick={() => onDownloadReport(session.sessionId)}
              className="me-2"
            >
              <i className="fas fa-download me-2"></i>
              Download Detailed Report
            </Button>
          )}
          
          {onRetryFailed && this.props.results.failed_count > 0 && (
            <Button
              variant="outline-warning"
              onClick={() => onRetryFailed(session.sessionId)}
              className="me-2"
            >
              <i className="fas fa-redo me-2"></i>
              Retry Failed Records
            </Button>
          )}
        </div>
        
        <div>
          {onClose && (
            <Button
              variant="success"
              onClick={onClose}
            >
              <i className="fas fa-check me-2"></i>
              Close Import
            </Button>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { results, session } = this.props;
    const { showDetailModal, selectedCategory, detailData } = this.state;

    if (!results || !session) {
      return (
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No import results available to display.
        </Alert>
      );
    }

    return (
      <div className="import-results-report">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Import Results Report
          </h3>
          <Badge bg="success" className="fs-6 px-3 py-2">
            Import Completed
          </Badge>
        </div>

        {this.renderSummaryCards()}
        {this.renderProcessingSummary()}
        {this.renderActionButtons()}

        {/* Detail Modal */}
        <Modal show={showDetailModal} onHide={this.closeDetailModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-list me-2"></i>
              {selectedCategory && selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Records Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.renderDetailTable(detailData, selectedCategory)}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeDetailModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <style jsx>{`
          .cursor-pointer {
            cursor: pointer;
          }
          .hover-shadow:hover {
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            transform: translateY(-2px);
            transition: all 0.3s ease;
          }
        `}</style>
      </div>
    );
  }
}